/* eslint-disable @typescript-eslint/member-ordering */

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  Output,
  ViewChild
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { IonInput } from '@ionic/angular';

let nextTextInputId = 0;

/** Usage: bind with formControlName and provide a visible label and optional error text. */
@Component({
  selector: 'app-text-input',
  templateUrl: './app-text-input.component.html',
  styleUrls: ['./app-text-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => AppTextInputComponent), multi: true }]
})
export class AppTextInputComponent implements ControlValueAccessor {
  constructor(private changeDetectorRef: ChangeDetectorRef) {}

  @Input() label = '';
  @Input() placeholder = '';
  @Input() required = false;
  @Input() errorText = '';
  @Input() type = 'text';
  @Input() maxLength: number | null = null;
  @Input() autocomplete = '';
  @Output() blurred = new EventEmitter<void>();
  readonly inputId = `app-text-input-${nextTextInputId++}`;
  @ViewChild(IonInput) private input?: IonInput;
  value = '';
  disabled = false;

  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  writeValue(value: string | null): void {
    this.value = value || '';
    this.changeDetectorRef.markForCheck();
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
    this.changeDetectorRef.markForCheck();
  }

  onInput(event: CustomEvent<{ value?: string | null }>): void {
    const inputElement = event.target as { value?: string | null } | null;
    this.value = event.detail?.value ?? inputElement?.value ?? '';
    this.onChange(this.value);
  }

  onBlur(): void {
    this.onTouched();
    this.blurred.emit();
  }

  setFocus(): Promise<void> {
    return this.input?.setFocus() || Promise.resolve();
  }
}
